import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, Package, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/orders/${orderId}`)
      .then(r => setOrder(r.data))
      .catch(err => console.error('API request failed:', err))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [orderId]);

  if (loading) return <div className="bg-[#F0F9FF] min-h-[60vh] flex items-center justify-center"><p className="text-[#6B7D8A] font-sans text-sm">Loading...</p></div>;
  if (!order) return <div className="bg-[#F0F9FF] min-h-[60vh] flex items-center justify-center"><p className="text-[#6B7D8A] font-sans text-sm">Order not found.</p></div>;

  return (
    <div className="bg-[#F0F9FF]">
      <section className="py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-10">
          {/* Success Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(186,230,253,0.12)', border: '2px solid rgba(186,230,253,0.3)' }}>
              <Check size={24} className="text-[#0C7BC0]" />
            </div>
            <h1 data-testid="confirmation-heading" className="text-2xl md:text-3xl font-serif font-medium text-[#0C4A8C] mb-2">Order Confirmed</h1>
            <p className="text-sm text-[#6B7D8A] font-sans">Thank you for your order, {order.customer?.name}.</p>
          </div>

          {/* Order Details Card */}
          <div className="bg-white rounded-[2px] p-6 mb-4" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }} data-testid="order-details">
            <div className="flex items-center justify-between mb-5 pb-4" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
              <div>
                <p className="text-[11px] font-sans uppercase tracking-[2px] text-[#6B7D8A] mb-1">Order ID</p>
                <p data-testid="confirmation-order-id" className="font-sans text-[15px] text-[#0C4A8C] font-medium">{order.order_id}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-sans uppercase tracking-[2px] text-[#6B7D8A] mb-1">Status</p>
                <span data-testid="confirmation-status" className="text-[10px] font-sans uppercase tracking-[2px] text-white bg-[#0C4A8C] px-2.5 py-1 rounded-[2px]">
                  {order.order_status}
                </span>
              </div>
            </div>

            {/* Items */}
            <div className="mb-5">
              <p className="text-[11px] font-sans uppercase tracking-[2px] text-[#6B7D8A] mb-3">Items</p>
              {(order.items || []).map((item, i) => (
                <div key={i} className="flex justify-between py-2" style={{ borderBottom: i < (order.items || []).length - 1 ? '1px solid rgba(12,74,140,0.06)' : 'none' }}>
                  <div>
                    <p className="text-sm text-[#0C4A8C] font-sans">{item.name}</p>
                    <p className="text-[11px] text-[#6B7D8A] font-sans">Qty: {item.quantity} {item.size && `· ${item.size}`}</p>
                  </div>
                  <span className="text-sm text-[#0C4A8C] font-sans">&#8377;{item.price * item.quantity}</span>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex justify-between pt-3" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
              <span className="text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C7BC0]">Total Paid</span>
              <span data-testid="confirmation-total" className="text-lg font-serif font-medium text-[#0C4A8C]">&#8377;{order.total_amount}</span>
            </div>
          </div>

          {/* Shipping Details */}
          {order.customer && (
            <div className="bg-white rounded-[2px] p-6 mb-4" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }} data-testid="shipping-details">
              <p className="text-[11px] font-sans uppercase tracking-[2px] text-[#6B7D8A] mb-3">Shipping To</p>
              <p className="text-sm text-[#0C4A8C] font-sans font-medium">{order.customer.name}</p>
              {order.customer.address && <p className="text-[13px] text-[#6B7D8A] font-sans mt-1">{order.customer.address}</p>}
              {(order.customer.city || order.customer.state) && (
                <p className="text-[13px] text-[#6B7D8A] font-sans">{[order.customer.city, order.customer.state, order.customer.pincode].filter(Boolean).join(', ')}</p>
              )}
              {order.customer.phone && <p className="text-[13px] text-[#6B7D8A] font-sans mt-1">{order.customer.phone}</p>}
              {order.customer.email && <p className="text-[13px] text-[#6B7D8A] font-sans">{order.customer.email}</p>}
            </div>
          )}

          {/* Delivery Info */}
          <div className="bg-white rounded-[2px] p-6 mb-6" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
            <div className="flex items-center gap-3">
              <Package size={18} className="text-[#0C7BC0]" />
              <div>
                <p className="text-sm text-[#0C4A8C] font-sans font-medium">Estimated Delivery</p>
                <p className="text-[12px] text-[#6B7D8A] font-sans">3-5 business days</p>
              </div>
            </div>
          </div>

          {/* Continue Shopping */}
          <div className="text-center">
            <Link to="/shop" data-testid="continue-shopping-btn"
              className="inline-flex items-center gap-2 bg-[#0C4A8C] text-[#F0F9FF] px-7 py-3 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors">
              Continue Shopping <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
